import React, { useEffect, useState, useCallback } from "react";
import { Tooltip, Spin } from "antd";
import { Context, Hooks, Util } from "@xt/xtbase";
import { get_inviteSummaryV2 } from "@/api/referral";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { Big, getUrl } = Util;

/** 与 GET /profile/invite/v2/summary 的 timeRange 一致 */
const TIME_RANGE_BY_CHIP = {
  "1": "ALL",
  "2": "YESTERDAY",
  "3": "LAST_7_DAYS",
  "4": "LAST_30_DAYS",
};

function InfoIcon() {
  return (
    <svg
      className={styles.infoIcon}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.25" fill="none" />
      <path fill="currentColor" d="M7.25 7.25h1.5v5h-1.5v-5Zm0-2.5h1.5V6h-1.5V4.75Z" />
    </svg>
  );
}

export default function MyTeam() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { loginStatus } = appState;
  const [loginQuery, setLoginQuery] = useState("");
  const [activeKey, setActiveKey] = useState("1");
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoginQuery("?backurl=" + encodeURIComponent(typeof window !== "undefined" ? window.location.href : ""));
  }, []);

  const changeActiveKey = (value) => {
    setActiveKey(value);
  };

  const loadData = useCallback(async () => {
    if (loginStatus !== 1) return;
    setLoading(true);
    try {
      const timeRange = TIME_RANGE_BY_CHIP[activeKey] || "ALL";
      const data = await get_inviteSummaryV2({
        params: {
          timeRange,
        },
      });
      setOverview(data || {});
    } finally {
      setLoading(false);
    }
  }, [loginStatus, activeKey]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fmtUsdt = (v) => `${Big(v ?? 0).toFixedMaxCy(2)} USDT`;
  const fmtInt = (v) => String(v ?? 0);

  const chips = [
    { key: "1", label: t("home.totalTime") },
    { key: "2", label: t("home.yesterday") },
    { key: "3", label: t("home.last7Days") },
    { key: "4", label: t("home.last30Days") },
  ];

  if (loginStatus !== 1) {
    return (
      <div className={styles.wrap}>
        <div className={styles.loginHint}>
          <a href={getUrl("/accounts/login" + loginQuery)} className={styles.loginLink}>
            {t("header.login")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.overviewRow}>
        <span className={styles.overviewTitle}>{t("home.screening")}</span>
        <Tooltip title={t("home.myTeamOverviewTip")} placement="topLeft">
          <span className={styles.infoWrap}>
            <InfoIcon />
          </span>
        </Tooltip>
      </div>

      <div className={styles.chips}>
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            className={activeKey === c.key ? styles.chipActive : styles.chip}
            onClick={() => changeActiveKey(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className={styles.card}>
        {loading ? (
          <div className={styles.cardLoading}>
            <Spin size="small" />
          </div>
        ) : (
          <>
            <div className={styles.cardRow}>
              <div className={styles.cell}>
                <div className={styles.cellLabel}>{t("home.myTeamTotalInvites")}</div>
                <div className={styles.cellValue}>{fmtInt(overview?.totalInvitations)}</div>
              </div>
              <div className={styles.cell}>
                <div className={styles.cellLabel}>{t("home.myTeamValidInvites")}</div>
                <div className={styles.cellValue}>{fmtInt(overview?.effectiveInvitations)}</div>
              </div>
            </div>
            <div className={styles.cardRow}>
              <div className={styles.cell}>
                <div className={styles.cellLabel}>{t("home.myTeamTradeCommission")}</div>
                <div className={`${styles.cellValue} ${styles.cellValueAccent}`}>
                  {fmtUsdt(overview?.tradingCommissionAmount)}
                </div>
              </div>
              <div className={styles.cell}>
                <div className={styles.cellLabel}>{t("home.myTeamInviteReward")}</div>
                <div className={`${styles.cellValue} ${styles.cellValueAccent}`}>
                  {fmtUsdt(overview?.inviteRewardAmount)}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
